import { useCallback, useEffect, useRef } from 'react';
import { User } from 'oidc-client-ts';
import { UseFormReturn } from 'react-hook-form';
import { useFeatureFlags } from 'features/DeveloperTools/hooks/useFeatureFlags';
import { FeatureFlagKeys } from 'features/DeveloperTools/config/constants';
import { useAppDispatch } from 'store/store';
import { setCredentials } from 'features/auth/authSlice';
import {
  useLazyGetUserEligibilityQuery,
  useLazyCodeCheckQuery,
  useGetSSOUserEligibilityMutation,
} from 'services/signupService';
import { useResetQueryParameters } from 'features/cobrand/hooks/useCobrandUtils';
import { useOidcClient } from 'features/auth/hooks/useOidcClient';
import { useSignupSectionNavigation } from './useSignupSectionNaivgation';
import { SignupErrorType, SignupFormData } from '../types';
import { useSignupData } from './useSignupData';
import { isUnderageError } from '../utils/errorUtils';
import { SignupError } from './useSignupError/SignupError';
import { useAccessCodeStep } from './useAccessCodeStep/useAccessCodeStep';
import { setSignUpState } from '../signUpSlice';

export const useSSOSignup = (
  useFormReturn: UseFormReturn<SignupFormData, any, undefined>,
  showError: (error: SignupErrorType) => void,
) => {
  const appDispatch = useAppDispatch();
  const { isEnabled } = useFeatureFlags();
  const [getEligibilityData] = useLazyGetUserEligibilityQuery();
  const [getSSOUserEligibility] = useGetSSOUserEligibilityMutation();
  const [codeCheck] = useLazyCodeCheckQuery();
  const { updateStore } = useAccessCodeStep();
  const { handleOIDCCallback, hasAuthCode, handleOIDCSignIn } = useOidcClient();
  const callbackExecutedRef = useRef(false);
  const resetQueryParameters = useResetQueryParameters();
  const { navigateToNextStep, resetNavigation } = useSignupSectionNavigation();
  const { isUserInvitedCaregiver } = useSignupData();
  const { setValue } = useFormReturn;

  const isSSOEligibilityChangeEnabled = isEnabled(FeatureFlagKeys.ELIGIBILITY_API_SSO_CHANGES);
  const fetchSSOEligibility = useCallback(
    async (code: string) => {
      if (isSSOEligibilityChangeEnabled) {
        return getSSOUserEligibility({ code });
      }
      return getEligibilityData({ code });
    },
    [isSSOEligibilityChangeEnabled, getSSOUserEligibility, getEligibilityData],
  );

  const onSSOLoginSuccess = useCallback(
    async (user: User) => {
      // accessCode is something we expect from successful SSO login
      // Which is different from access_token, access_token is the token used for API calls
      const userState = user.state as {
        accessCode: string;
      };

      if (user.access_token && user.refresh_token) {
        appDispatch(
          setCredentials({
            accessToken: user.access_token,
            refreshToken: user.refresh_token,
          }),
        );
      }

      const [eligibilityResult, codeCheckResult] = await Promise.all([
        fetchSSOEligibility(userState.accessCode),
        codeCheck(userState.accessCode),
      ]);
      const { data: eligibilityData, error: eligibilityError } = eligibilityResult;
      const { data: codeCheckData, error: codeCheckError } = codeCheckResult;

      if (eligibilityError) {
        if (isUnderageError(eligibilityError)) {
          throw new SignupError(
            'You are under the minimum age limit to create an account.',
            'UnderageUser',
          );
        }
        throw new SignupError(
          'An error occurred while fetching eligibility data.',
          'IneligibleSSOUser',
        );
      }

      if (codeCheckError || !codeCheckData) {
        throw new SignupError('An error occurred while re-checking the access code.', 'Generic');
      }

      // Since the redux store resets when the tab closes/reopens during SSO, we need update the store with the
      // code check (privacy policy, EULA) data once more.
      updateStore(codeCheckData);

      // Set the form values
      setValue('accessCode', userState.accessCode);

      if (eligibilityData) {
        setValue('memberFirstName', eligibilityData.first_name);
        setValue('memberLastName', eligibilityData.last_name);
        setValue('memberDateOfBirth', eligibilityData.dob);
        setValue('memberGender', eligibilityData.gender);
        setValue('memberPhoneNumber', eligibilityData.phone_number);
        // To prefill email fields for member profile screen in SSO signups
        setValue('memberProfileEmail', eligibilityData.email);
        setValue('memberId', eligibilityData.member_id);

        if (eligibilityData.caregiver && isUserInvitedCaregiver) {
          setValue('caregiverFirstName', eligibilityData.caregiver.first_name);
          setValue('caregiverLastName', eligibilityData.caregiver.last_name);
          setValue('caregiverPhoneNumber', eligibilityData.caregiver.phone_number);
        }

        // Update the sign up state in the redux store
        appDispatch(
          setSignUpState({
            wellframeUserId: eligibilityData.id,
            memberId: eligibilityData.member_id,
            activeUserToken: eligibilityData.active_user_token,
            isUserApproved: eligibilityData.approved_status,
            userSignupStatus: eligibilityData.status,
            ssoAuthConfig: {
              integrationId: eligibilityData.auth?.integration_id ?? '',
              authUserId: eligibilityData.auth?.auth_user_id ?? '',
            },
          }),
        );
      }

      resetQueryParameters();
      navigateToNextStep('SSOSignUpSuccess');
    },
    [
      appDispatch,
      fetchSSOEligibility,
      navigateToNextStep,
      codeCheck,
      updateStore,
      isUserInvitedCaregiver,
      setValue,
      resetQueryParameters,
    ],
  );

  const onSSOLoginError = useCallback(
    (error: SignupError) => {
      resetQueryParameters();
      resetNavigation();
      showError(error.type);
    },
    [resetQueryParameters, resetNavigation, showError],
  );

  useEffect(() => {
    if (!hasAuthCode || callbackExecutedRef.current) {
      return;
    }

    callbackExecutedRef.current = true;
    navigateToNextStep('SSOSignupLoading');

    const continueSSOSignup = async () => {
      try {
        const user = await handleOIDCCallback();
        await onSSOLoginSuccess(user);
      } catch (error) {
        onSSOLoginError(error as SignupError);
      }
    };
    continueSSOSignup();
  }, [hasAuthCode, navigateToNextStep, handleOIDCCallback, onSSOLoginSuccess, onSSOLoginError]);

  return {
    handleOIDCSignIn,
    hasAuthCode,
  };
};
